import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatNumber } from './utils';
import { type CropConfig, type DemoFarmInputs, type TestPacketsInputs, type AreaUnit, type WeightUnit, AREA_LABELS, WEIGHT_LABELS, AREA_FACTORS, WEIGHT_FACTORS } from './types';

interface Props {
  currencyCode: string;
  exchangeRate: number;
  areaUnit: AreaUnit;
  weightUnit: WeightUnit;
  demoFarm: DemoFarmInputs;
  testPackets: TestPacketsInputs;
  crops: CropConfig[];
}

export function Instructions({ currencyCode, exchangeRate, areaUnit, weightUnit, demoFarm, testPackets, crops }: Props) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'usage' | 'formulas'>('usage');

  return (
    <div className="card !p-4 md:!p-5 bg-gradient-to-br from-blue-50 to-white border border-blue-100">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center">
          <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-sm font-semibold text-blue-900">{t('instructions.guideTitle')}</h3>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-3 border-b border-blue-100 pb-2">
        <button
          onClick={() => setActiveTab('usage')}
          className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
            activeTab === 'usage'
              ? 'bg-blue-600 text-white'
              : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
          }`}
        >
          {t('instructions.tabUsage')}
        </button>
        <button
          onClick={() => setActiveTab('formulas')}
          className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
            activeTab === 'formulas'
              ? 'bg-blue-600 text-white'
              : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
          }`}
        >
          {t('instructions.tabFormulas')}
        </button>
      </div>

      {activeTab === 'usage' ? (
        <div className="space-y-3 text-xs text-blue-900">
          <div>
            <h4 className="font-semibold mb-1 flex items-center gap-1.5">
              <span className="w-5 h-5 rounded-full bg-spi-green text-white flex items-center justify-center text-[10px]">1</span>
              {t('instructions.purposeTitle')}
            </h4>
            <p className="ml-6 text-blue-800">
              {t('instructions.purposeDescription')}
            </p>
            <ul className="ml-6 mt-1 space-y-0.5 text-blue-700">
              <li className="flex items-start gap-1.5">
                <span className="text-spi-green mt-0.5">●</span>
                <span><strong>{t('instructions.demoFarmsLabel')}</strong> {t('instructions.demoFarmsDescription')}</span>
              </li>
              <li className="flex items-start gap-1.5">
                <span className="text-spi-orange mt-0.5">●</span>
                <span><strong>{t('instructions.testPacketsLabel')}</strong> {t('instructions.testPacketsDescription')}</span>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-1 flex items-center gap-1.5">
              <span className="w-5 h-5 rounded-full bg-spi-green text-white flex items-center justify-center text-[10px]">2</span>
              {t('instructions.demoFarmInputsTitle')}
            </h4>
            <ul className="ml-6 space-y-0.5 text-blue-700">
              <li><strong>{t('instructions.farmSizeHa')}</strong> {t('instructions.farmSizeDescription')}</li>
              <li><strong>{t('instructions.cropPercent')}</strong> {t('instructions.cropPercentDescription')}</li>
              <li><strong>{t('instructions.seedRatesLabel')}</strong> {t('instructions.seedRatesDescription', { weight: WEIGHT_LABELS[weightUnit], area: AREA_LABELS[areaUnit] })}</li>
              <li><strong>{t('instructions.seedCostsLabel')}</strong> {t('instructions.seedCostsDescription', { weight: WEIGHT_LABELS[weightUnit], currency: currencyCode })}</li>
            </ul>
          </div>

          <div>
            <h4 className="font-semibold mb-1 flex items-center gap-1.5">
              <span className="w-5 h-5 rounded-full bg-spi-green text-white flex items-center justify-center text-[10px]">3</span>
              {t('instructions.testPacketsInputsTitle')}
            </h4>
            <ul className="ml-6 space-y-0.5 text-blue-700">
              <li><strong>{t('instructions.householdsLabel')}</strong> {t('instructions.householdsDescription')}</li>
              <li><strong>{t('instructions.packetsPerHH')}</strong> {t('instructions.packetsPerHHDescription')}</li>
              <li><strong>{t('instructions.packetWeightsLabel')}</strong> {t('instructions.packetWeightsDescription', { weight: WEIGHT_LABELS[weightUnit] })}</li>
            </ul>
          </div>

          <div className="bg-blue-100/50 p-2.5 rounded-lg">
            <h4 className="font-semibold mb-1">{t('instructions.quickTips')}</h4>
            <ul className="space-y-0.5 text-blue-700">
              <li>● {t('instructions.tipAutoUpdate')}</li>
              <li>● {t('instructions.tipPercentages')}</li>
              {currencyCode !== 'USD' && (
                <li>● {t('instructions.tipRate', { rate: formatNumber(exchangeRate, 2), currency: currencyCode })}</li>
              )}
            </ul>
          </div>
        </div>
      ) : (
        <DynamicFormulas
          crops={crops}
          demoFarm={demoFarm}
          testPackets={testPackets}
          areaUnit={areaUnit}
          weightUnit={weightUnit}
          currencyCode={currencyCode}
          exchangeRate={exchangeRate}
        />
      )}
    </div>
  );
}

function DynamicFormulas({
  crops, demoFarm, testPackets, areaUnit, weightUnit, currencyCode, exchangeRate,
}: {
  crops: CropConfig[];
  demoFarm: DemoFarmInputs;
  testPackets: TestPacketsInputs;
  areaUnit: AreaUnit;
  weightUnit: WeightUnit;
  currencyCode: string;
  exchangeRate: number;
}) {
  const { t } = useTranslation();
  const a = AREA_LABELS[areaUnit];
  const w = WEIGHT_LABELS[weightUnit];
  const aF = AREA_FACTORS[areaUnit];
  const wF = WEIGHT_FACTORS[weightUnit];

  // Use first crop for live example
  const crop = crops[0];
  const cropLabel = crop?.label ?? 'Crop';
  const cropId = crop?.id ?? '';

  // Demo farm values (converted to display units)
  const farmDisplay = formatNumber(demoFarm.farmHectares * aF, 2);
  const pct = demoFarm.cropPercentages[cropId] ?? 0;
  const cropAreaHa = demoFarm.farmHectares * (pct / 100);
  const cropAreaDisplay = formatNumber(cropAreaHa * aF, 2);
  const seedRateBase = demoFarm.seedRatesKgPerHa[cropId] ?? 0;
  // Rate converts: (kg/ha) → (displayWeight / displayArea)
  const seedRateDisplay = formatNumber((seedRateBase * wF) / aF, 2);
  const seedKg = cropAreaHa * seedRateBase;
  const seedDisplay = formatNumber(seedKg * wF, 2);
  const priceBase = demoFarm.seedCostsLocalPerKg[cropId] ?? 0;
  // Price converts: (currency/kg) → (currency/displayWeight)
  const priceDisplay = formatNumber(priceBase / wF, 2);
  const costLocal = seedKg * priceBase;
  const costLocalDisplay = formatNumber(costLocal, 0);
  const costUsd = costLocal / exchangeRate;
  const costUsdDisplay = formatNumber(costUsd, 2);

  // Test packets values
  const refHH = testPackets.refugeeHouseholds;
  const hostHH = testPackets.hostCommunityHouseholds;
  const refPkts = testPackets.avgPacketsPerRefugeeHousehold;
  const hostPkts = testPackets.avgPacketsPerHostHousehold;
  const totalPackets = refHH * refPkts + hostHH * hostPkts;
  const packetWeightKg = testPackets.packetWeightsKg[cropId] ?? 0;
  const packetWeightDisplay = formatNumber(packetWeightKg * wF, 4);
  const tpSeedKg = totalPackets * packetWeightKg;
  const tpSeedDisplay = formatNumber(tpSeedKg * wF, 2);
  const equivHa = tpSeedKg / (seedRateBase || 1);
  const equivDisplay = formatNumber(equivHa * aF, 2);
  const tpCostLocal = tpSeedKg * priceBase;
  const tpCostDisplay = formatNumber(tpCostLocal, 0);

  const isUsd = currencyCode === 'USD';

  return (
    <div className="space-y-3 text-xs">
      <div className="bg-spi-green/5 p-3 rounded-lg border border-spi-green/10">
        <h4 className="font-semibold text-spi-green-dark mb-2 flex items-center gap-1.5 text-xs">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
          {t('instructions.demoFarmCalcTitle')}
        </h4>

        <div className="space-y-2 text-spi-charcoal">
          <div className="bg-white p-2.5 rounded">
            <p className="font-medium mb-1">{t('instructions.step1AreaTitle')}</p>
            <code className="block bg-spi-seed px-2 py-1 rounded text-xs font-mono">
              {t('instructions.step1AreaFormula', { area: a })}
            </code>
            <p className="text-[10px] text-spi-slate mt-1">
              {farmDisplay} {a} × ({pct}% ÷ 100) = <strong>{cropAreaDisplay} {a}</strong> {cropLabel}
            </p>
          </div>

          <div className="bg-white p-2.5 rounded">
            <p className="font-medium mb-1">{t('instructions.step2SeedTitle')}</p>
            <code className="block bg-spi-seed px-2 py-1 rounded text-xs font-mono">
              {t('instructions.step2SeedFormula', { weight: w, area: a })}
            </code>
            <p className="text-[10px] text-spi-slate mt-1">
              {cropAreaDisplay} {a} × {seedRateDisplay} {w}/{a} = <strong>{seedDisplay} {w}</strong> {cropLabel}
            </p>
          </div>

          <div className="bg-white p-2.5 rounded">
            <p className="font-medium mb-1">{t('instructions.step3CostTitle')}</p>
            <code className="block bg-spi-seed px-2 py-1 rounded text-xs font-mono">
              {t('instructions.step3CostFormulaLocal', { currency: currencyCode, weight: w })}
            </code>
            <p className="text-[10px] text-spi-slate mt-1">
              {seedDisplay} {w} × {priceDisplay} {currencyCode}/{w} = <strong>{costLocalDisplay} {currencyCode}</strong>
            </p>
            {!isUsd && (
              <>
                <code className="block bg-spi-seed px-2 py-1 rounded text-xs font-mono mt-1">
                  {t('instructions.step3CostFormulaUsd', { currency: currencyCode, rate: formatNumber(exchangeRate, 2) })}
                </code>
                <p className="text-[10px] text-spi-slate mt-1">
                  {costLocalDisplay} {currencyCode} ÷ {formatNumber(exchangeRate, 2)} = <strong>${costUsdDisplay}</strong>
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="bg-spi-orange/5 p-3 rounded-lg border border-spi-orange/10">
        <h4 className="font-semibold text-spi-earth mb-2 flex items-center gap-1.5 text-xs">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
          {t('instructions.testPacketsCalcTitle')}
        </h4>

        <div className="space-y-2 text-spi-charcoal">
          <div className="bg-white p-2.5 rounded">
            <p className="font-medium mb-1">{t('instructions.step1PacketsTitle')}</p>
            <code className="block bg-spi-seed px-2 py-1 rounded text-xs font-mono">
              {t('instructions.step1PacketsFormula')}
            </code>
            <p className="text-[10px] text-spi-slate mt-1">
              ({formatNumber(refHH, 0)} × {formatNumber(refPkts, 1)}) + ({formatNumber(hostHH, 0)} × {formatNumber(hostPkts, 1)}) = <strong>{formatNumber(totalPackets, 0)}</strong>
            </p>
          </div>

          <div className="bg-white p-2.5 rounded">
            <p className="font-medium mb-1">{t('instructions.step2SeedPerCropTitle')}</p>
            <code className="block bg-spi-seed px-2 py-1 rounded text-xs font-mono">
              {t('instructions.step2SeedPerCropFormula', { weight: w })}
            </code>
            <p className="text-[10px] text-spi-slate mt-1">
              {formatNumber(totalPackets, 0)} × {packetWeightDisplay} {w} = <strong>{tpSeedDisplay} {w}</strong> {cropLabel}
            </p>
          </div>

          <div className="bg-white p-2.5 rounded">
            <p className="font-medium mb-1">{t('instructions.step3EquivTitle')}</p>
            <code className="block bg-spi-seed px-2 py-1 rounded text-xs font-mono">
              {t('instructions.step3EquivFormula', { area: a, weight: w })}
            </code>
            <p className="text-[10px] text-spi-slate mt-1">
              {tpSeedDisplay} {w} ÷ {seedRateDisplay} {w}/{a} = <strong>{equivDisplay} {a}</strong>
            </p>
          </div>

          <div className="bg-white p-2.5 rounded">
            <p className="font-medium mb-1">{t('instructions.step4CostTitle')}</p>
            <code className="block bg-spi-seed px-2 py-1 rounded text-xs font-mono">
              {t('instructions.step4CostFormula', { currency: currencyCode, weight: w })}
            </code>
            <p className="text-[10px] text-spi-slate mt-1">
              {tpSeedDisplay} {w} × {priceDisplay} {currencyCode}/{w} = <strong>{tpCostDisplay} {currencyCode}</strong>
            </p>
          </div>
        </div>
      </div>

      {!isUsd && (
        <div className="bg-gray-50 p-2 rounded-lg text-center text-spi-slate">
          <p className="text-[10px]">
            {t('instructions.exchangeRateNote', { rate: formatNumber(exchangeRate, 2), currency: currencyCode })}
          </p>
        </div>
      )}
    </div>
  );
}
